import React, { useState, useEffect, useMemo } from 'react';
import PlotlyChart from '../../components/charts/PlotlyChart';
import { hfService } from '../../services/hfService';
import { parseDfJson, formatPct } from '../../utils/formatters';
import { getFundColor } from '../../utils/colors';

const WINDOWS = [3, 6, 12, 24, 36, 60];

const RollingPage = ({ analyticsParams, focusFund, cachedFetch }) => {
  const [window, setWindow] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('all');
  const [showSPX, setShowSPX] = useState(false);

  useEffect(() => {
    if (!analyticsParams?.dataset_id || !analyticsParams?.funds?.length) return;

    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchFn = () => hfService.getRolling({ ...analyticsParams, window });
        const res = cachedFetch ? await cachedFetch('rolling-' + window, fetchFn) : await fetchFn();
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [analyticsParams, window]);

  /* --- Build chart traces --- */
  const { returnTraces, returnLayout, statsRows, varTraces, varLayout, glTraces, glLayout, sharpeTraces, sharpeLayout, sortinoTraces, sortinoLayout } =
    useMemo(() => {
      if (!data) return {};

      const { rolling_return, colors, stats, rolling_var, rolling_cvar, rolling_gl, rolling_sharpe, rolling_sortino } = data;
      const rr = parseDfJson(rolling_return);

      /* Rolling return line chart */
      const returnTraces = rr.columns.map((fund, colIdx) => ({
        x: rr.index,
        y: rr.data.map(row => row[colIdx]),
        name: fund,
        type: 'scatter',
        mode: 'lines',
        line: { color: getFundColor(fund, colors), width: 1.5 },
        hovertemplate: `%{x|%b %Y}<br>${fund}: %{y:.2%}<extra></extra>`,
      }));

      const returnLayout = {
        title: `Rolling ${window}-Month Return`,
        yaxis: { title: 'Return', tickformat: '.1%', gridcolor: '#eee', zerolinecolor: '#ddd' },
        xaxis: { gridcolor: '#eee', zerolinecolor: '#ddd' },
      };

      /* Stats table rows */
      const statsRows = Object.entries(stats || {}).map(([fund, s]) => ({
        fund,
        color: getFundColor(fund, colors),
        mean: s.mean,
        std: s.std,
        min: s.min,
        max: s.max,
        median: s.median,
        pct_positive: s.pct_positive,
        pct_above_8: s.pct_above_8,
      }));

      /* Rolling VaR / CVaR chart */
      const varDf = parseDfJson(rolling_var);
      const cvarDf = parseDfJson(rolling_cvar);

      const varTraces = [];
      varDf.columns.forEach((fund, colIdx) => {
        const c = getFundColor(fund, colors);
        varTraces.push({
          x: varDf.index,
          y: varDf.data.map(row => row[colIdx]),
          name: `${fund} VaR`,
          type: 'scatter',
          mode: 'lines',
          connectgaps: true,
          line: { color: c, width: 1.5, dash: 'solid' },
          hovertemplate: `%{x|%b %Y}<br>${fund} VaR: %{y:.2%}<extra></extra>`,
        });
      });
      cvarDf.columns.forEach((fund, colIdx) => {
        const c = getFundColor(fund, colors);
        varTraces.push({
          x: cvarDf.index,
          y: cvarDf.data.map(row => row[colIdx]),
          name: `${fund} CVaR`,
          type: 'scatter',
          mode: 'lines',
          connectgaps: true,
          line: { color: c, width: 1.5, dash: 'dash' },
          legendgroup: fund,
          hovertemplate: `%{x|%b %Y}<br>${fund} CVaR: %{y:.2%}<extra></extra>`,
        });
      });

      const varLayout = {
        title: `Rolling ${window}-Month VaR & CVaR`,
        yaxis: { title: 'Value at Risk', tickformat: '.1%', gridcolor: '#eee', zerolinecolor: '#ddd' },
        xaxis: { gridcolor: '#eee', zerolinecolor: '#ddd' },
      };

      /* Rolling gain/loss ratio chart */
      const glDf = parseDfJson(rolling_gl);
      const glTraces = glDf.columns.map((fund, colIdx) => ({
        connectgaps: true,
        x: glDf.index,
        y: glDf.data.map(row => row[colIdx]),
        name: fund,
        type: 'scatter',
        mode: 'lines',
        line: { color: getFundColor(fund, colors), width: 1.5 },
        hovertemplate: `%{x|%b %Y}<br>${fund}: %{y:.2f}<extra></extra>`,
      }));

      const glLayout = {
        title: `Rolling ${window}-Month Gain/Loss Ratio`,
        yaxis: { title: 'Gain/Loss Ratio', tickformat: '.2f', gridcolor: '#eee', zerolinecolor: '#ddd', range: [0, 10] },
        xaxis: { gridcolor: '#eee', zerolinecolor: '#ddd' },
        shapes: [
          {
            type: 'line',
            x0: 0, x1: 1, xref: 'paper',
            y0: 1, y1: 1,
            line: { color: '#999', width: 1, dash: 'dot' },
          },
        ],
      };

      /* Rolling Sharpe ratio chart (annualized) */
      const sharpeDf = rolling_sharpe ? parseDfJson(rolling_sharpe) : { columns: [], index: [], data: [] };
      const sharpeTraces = sharpeDf.columns.map((fund, colIdx) => ({
        connectgaps: true,
        x: sharpeDf.index,
        y: sharpeDf.data.map(row => row[colIdx]),
        name: fund,
        type: 'scatter',
        mode: 'lines',
        line: { color: getFundColor(fund, colors), width: 1.5 },
        hovertemplate: `%{x|%b %Y}<br>${fund}: %{y:.2f}<extra></extra>`,
      }));
      const sharpeLayout = {
        title: `Rolling ${window}-Month Sharpe Ratio`,
        yaxis: { title: 'Sharpe (annualized)', tickformat: '.2f', gridcolor: '#eee', zerolinecolor: '#ddd' },
        xaxis: { gridcolor: '#eee', zerolinecolor: '#ddd' },
        shapes: [{
          type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0,
          line: { color: '#999', width: 1, dash: 'dot' },
        }],
      };

      /* Rolling Sortino ratio chart (annualized) */
      const sortinoDf = rolling_sortino ? parseDfJson(rolling_sortino) : { columns: [], index: [], data: [] };
      const sortinoTraces = sortinoDf.columns.map((fund, colIdx) => ({
        connectgaps: true,
        x: sortinoDf.index,
        y: sortinoDf.data.map(row => row[colIdx]),
        name: fund,
        type: 'scatter',
        mode: 'lines',
        line: { color: getFundColor(fund, colors), width: 1.5 },
        hovertemplate: `%{x|%b %Y}<br>${fund}: %{y:.2f}<extra></extra>`,
      }));
      const sortinoLayout = {
        title: `Rolling ${window}-Month Sortino Ratio`,
        yaxis: { title: 'Sortino (annualized)', tickformat: '.2f', gridcolor: '#eee', zerolinecolor: '#ddd' },
        xaxis: { gridcolor: '#eee', zerolinecolor: '#ddd' },
        shapes: [{
          type: 'line', x0: 0, x1: 1, xref: 'paper', y0: 0, y1: 0,
          line: { color: '#999', width: 1, dash: 'dot' },
        }],
      };

      // Remove null/NaN y-values from a trace so Plotly auto-scales x-axis to data range
      const stripNulls = (t) => {
        const filtered = t.x.reduce((acc, x, i) => {
          if (t.y[i] != null && !isNaN(t.y[i])) { acc.x.push(x); acc.y.push(t.y[i]); }
          return acc;
        }, { x: [], y: [] });
        return { ...t, x: filtered.x, y: filtered.y };
      };

      // Filter traces based on viewMode + SPX toggle
      const filterTraces = (traces) => {
        let filtered = traces;
        // Hide SPX unless toggled on
        if (!showSPX) {
          filtered = filtered.filter(t => {
            const name = t.name || '';
            return !name.includes('SPX');
          });
        }
        if (viewMode === 'focus' && focusFund) {
          filtered = filtered.filter(t => {
            const name = t.name || '';
            return name === focusFund || name.startsWith(focusFund + ' ') || (showSPX && name.includes('SPX'));
          });
        }
        return filtered.map(stripNulls);
      };

      // Filter stats rows
      const filteredStatsRows = viewMode === 'focus' && focusFund
        ? statsRows.filter(r => r.fund === focusFund)
        : statsRows;

      return {
        returnTraces: filterTraces(returnTraces),
        returnLayout,
        statsRows: filteredStatsRows,
        varTraces: filterTraces(varTraces),
        varLayout,
        glTraces: filterTraces(glTraces),
        glLayout,
        sharpeTraces: filterTraces(sharpeTraces),
        sharpeLayout,
        sortinoTraces: filterTraces(sortinoTraces),
        sortinoLayout,
      };
    }, [data, window, viewMode, focusFund, showSPX]);

  if (!analyticsParams?.dataset_id) {
    return (
      <div className="rounded-lg border border-blue-300 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-700 dark:text-blue-400">
        Select a dataset and funds to view rolling analysis.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-brand-gold" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Calculating rolling {window}-month metrics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
        {error}
      </div>
    );
  }

  if (!data) return null;

  return (
    <div>
      {/* Window selector */}
      <div className="flex items-center gap-3 flex-wrap mb-4">
        <span className="text-sm font-semibold text-gray-500 dark:text-gray-400">Rolling Window:</span>
        <div className="flex gap-1">
          {WINDOWS.map(w => (
            <button
              key={w}
              onClick={() => setWindow(w)}
              className={`px-3 py-1 text-xs rounded transition-colors ${
                w === window
                  ? 'bg-brand-gold text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700'
              }`}
            >
              {w}M
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setViewMode('all')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${viewMode === 'all' ? 'bg-brand-gold text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`}>All Funds</button>
        <button onClick={() => setViewMode('focus')} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${viewMode === 'focus' ? 'bg-brand-gold text-white' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`}>{focusFund ? focusFund.split(' ')[0] + ' Only' : 'Focus Fund'}</button>
        <span className="w-px h-6 bg-gray-300 dark:bg-gray-700 self-center" />
        <button onClick={() => setShowSPX(!showSPX)} className={`px-3 py-1.5 text-xs rounded-md transition-colors ${showSPX ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-400 border border-gray-200 dark:border-gray-700'}`}>{showSPX ? 'SPX \u2713' : 'SPX'}</button>
      </div>

      {/* Rolling return line chart */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
        <PlotlyChart data={returnTraces} layout={returnLayout} />
      </div>

      {/* Rolling return statistics table */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden mb-4">
        <div className="px-4 py-3 font-semibold text-sm text-gray-800 dark:text-gray-200 border-b border-gray-200 dark:border-gray-800">
          Rolling {window}-Month Return Statistics
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-xs uppercase tracking-wider text-gray-500">
              <tr>
                <th className="px-3 py-2.5 text-left">Fund</th>
                <th className="px-3 py-2.5 text-right">Mean</th>
                <th className="px-3 py-2.5 text-right">Std Dev</th>
                <th className="px-3 py-2.5 text-right">Min</th>
                <th className="px-3 py-2.5 text-right">Max</th>
                <th className="px-3 py-2.5 text-right">Median</th>
                <th className="px-3 py-2.5 text-right">% &gt; 0%</th>
                <th className="px-3 py-2.5 text-right">% &gt; 8%</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {(statsRows || []).map((row) => (
                <tr key={row.fund} className="hover:bg-gray-50 dark:hover:bg-gray-800/30">
                  <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                    <span
                      className="inline-block mr-2 rounded-full"
                      style={{
                        width: 10,
                        height: 10,
                        backgroundColor: row.color,
                      }}
                    />
                    {row.fund}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatPct(row.mean)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatPct(row.std)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-red-500">{formatPct(row.min)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-green-500">{formatPct(row.max)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{formatPct(row.median)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{row.pct_positive != null ? row.pct_positive.toFixed(1) + '%' : 'N/A'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">{row.pct_above_8 != null ? row.pct_above_8.toFixed(1) + '%' : 'N/A'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Rolling Sharpe ratio chart */}
      {sharpeTraces && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Rolling Sharpe Ratio</h3>
          <PlotlyChart data={sharpeTraces} layout={{ ...sharpeLayout, title: '' }} />
        </div>
      )}

      {/* Rolling Sortino ratio chart */}
      {sortinoTraces && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Rolling Sortino Ratio</h3>
          <PlotlyChart data={sortinoTraces} layout={{ ...sortinoLayout, title: '' }} />
        </div>
      )}

      {/* Rolling VaR / CVaR chart */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Rolling VaR & CVaR</h3>
        <PlotlyChart data={varTraces} layout={{ ...varLayout, title: '' }} />
      </div>

      {/* Rolling gain/loss ratio chart */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
        <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">Rolling Gain/Loss Ratio</h3>
        <PlotlyChart data={glTraces} layout={{ ...glLayout, title: '' }} />
      </div>
    </div>
  );
};

export default RollingPage;
